import _ from "lodash";

import { db } from "../../main";
import { IresDevice } from "../models/IresDevice";

/** Firestore allows at most ten values in an `in` filter. */
const MAX_IN_VALUES = 10;

/** Offset that maps a chunk index to the index its cache seed is published under. */
const SEED_CHUNK_OFFSET = -1000;

/**
 * Observes every device of one collection assigned to any of the given client ids.
 *
 * Replaces the previous one-listener-per-client fan-out. A master client has dozens of
 * member clients, and the device list and the cockpit each opened one listener per
 * member per collection: for an SVT master session that was 78 listeners for the
 * devices alone, against Firestore's documented limit of 100 snapshot listeners per
 * client. Chunked `in` filters need `ceil(clients / 10)` listeners instead - four
 * rather than thirty-nine. `currentAssignment.clientId` is a single equality field, so
 * the automatic single-field index covers this and no composite index is needed.
 *
 * Two properties the callers depend on:
 *
 * - **Removals are applied.** The previous implementation only ever added to its map,
 *   so a device moved to a client outside the group stayed in the list for the rest of
 *   the session.
 * - **Unchanged devices keep their object identity.** Only the documents Firestore
 *   reports as changed are deserialized again, so the device list can tell which rows
 *   actually need re-rendering. Re-mapping every document of every snapshot would hand
 *   every row a new object and defeat that.
 *
 * The chunks partition the devices by client id, so their result sets are disjoint and
 * merging is an overwrite by serial number. While a device moves between two member
 * clients its two snapshots can arrive in either order, so merging by serial (rather
 * than concatenating) is what keeps it from appearing twice.
 */
export function observeDevicesOfClients<T extends IresDevice>(
  collectionName: string,
  clientIds: Array<string>,
  onNext: (devices: Array<T>) => void,
  seedDevices?: Array<T>
) {
  const chunks = _.chunk(clientIds, MAX_IN_VALUES);
  const devicesByChunk = new Map<number, Map<string, T>>();

  // The seed is split per chunk and each part is dropped when its own chunk answers,
  // so a seeded device only ever disappears once the query responsible for it has said
  // so. Dropping the whole seed as soon as every chunk had answered once made the
  // published set shrink and then grow again, which showed up as the cockpit counters
  // resetting mid-load.
  if (seedDevices?.length) {
    const chunkIndexByClientId = new Map<string, number>();

    chunks.forEach((chunk, chunkIndex) =>
      chunk.forEach((clientId) => chunkIndexByClientId.set(clientId, chunkIndex))
    );

    seedDevices.forEach((device) => {
      const clientId = device.currentAssignment?.clientId;
      const chunkIndex = clientId
        ? chunkIndexByClientId.get(clientId)
        : undefined;

      if (chunkIndex === undefined) {
        return;
      }

      const seedIndex = chunkIndex + SEED_CHUNK_OFFSET;
      const seededDevices = devicesByChunk.get(seedIndex) ?? new Map<string, T>();

      seededDevices.set(device.serialNo, device);
      devicesByChunk.set(seedIndex, seededDevices);
    });
  }

  const publish = () => {
    const merged = new Map<string, T>();

    devicesByChunk.forEach((devicesOfChunk) =>
      devicesOfChunk.forEach((device, serialNumber) =>
        merged.set(serialNumber, device)
      )
    );

    onNext(Array.from(merged.values()));
  };

  if (devicesByChunk.size > 0) {
    publish();
  }

  const observeChunk = (chunk: Array<string>, chunkIndex: number) => {
    const devicesOfChunk = new Map<string, T>();
    devicesByChunk.set(chunkIndex, devicesOfChunk);

    return db
      .collection(collectionName)
      .where("currentAssignment.clientId", "in", chunk)
      .onSnapshot((snapshot) => {
        const arrivedSerialNumbers: Array<string> = [];

        snapshot.docChanges().forEach((change) => {
          const serialNumber = change.doc.id;

          if (change.type === "removed") {
            devicesOfChunk.delete(serialNumber);
            return;
          }

          devicesOfChunk.set(serialNumber, change.doc.data() as T);
          arrivedSerialNumbers.push(serialNumber);
        });

        // A device that moved into this chunk is no longer owned by the chunk it came
        // from. Dropping the stale copy here keeps the move from showing twice until
        // the other chunk reports the removal, whichever order they arrive in.
        devicesByChunk.forEach((otherDevices, otherChunkIndex) => {
          if (otherChunkIndex === chunkIndex) {
            return;
          }

          arrivedSerialNumbers.forEach((serialNumber) =>
            otherDevices.delete(serialNumber)
          );
        });

        // This chunk's query is now authoritative for its clients, so its share of the
        // cache seed has done its job. Keeping it would resurrect devices that have
        // since been removed.
        devicesByChunk.delete(chunkIndex + SEED_CHUNK_OFFSET);

        publish();
      });
  };

  const observers = chunks.map(observeChunk);

  return () => {
    observers.forEach((observer) => observer());
  };
}
